// External Stats Service - Direct API Communication
// Handles communication with external stats API directly via HTTPS

import { createErrorHandler } from '../context/shared/errorHandler.js'

// Create error handler for this service
const errorHandler = createErrorHandler('ExternalStatsService')

// API configuration
// Override with REACT_APP_STATS_API_URL for local dev (e.g. the ugg-scraper on
// http://localhost:8080); falls back to the production stats API.
const API_BASE_URL = process.env.REACT_APP_STATS_API_URL || 'https://stats-api.vrgbackend.com'
const REQUEST_TIMEOUT = 30000 // 30 seconds

// Cache for API responses to reduce repeated calls
const cache = new Map()
const CACHE_DURATION = 2 * 60 * 1000 // 2 minutes

// Helper function to convert league/division to readable rank
const getRankDisplay = (league, division) => {
  const rankMap = {
    0: 'Bronze',
    1: 'Silver', 
    2: 'Gold',
    3: 'Platinum',
    4: 'Diamond',
    5: 'Master',
    6: 'Elite',
    7: 'Supersonic Legend'
  }
  
  const rankName = rankMap[league] || 'Unranked'
  if (league === 6 || league === 7) return rankName // Elite and Supersonic Legend have no divisions
  
  const divisionNames = ['III', 'II', 'I']
  const divisionName = divisionNames[division] || 'III'
  
  return `${rankName} ${divisionName}`
}

// Helper function to get cached response
const getCachedResponse = (key) => {
  const cached = cache.get(key)
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data
  }
  cache.delete(key)
  return null
}

// Helper function to cache response
const setCachedResponse = (key, data) => {
  cache.set(key, {
    data,
    timestamp: Date.now()
  })
  
  // Clean up old cache entries periodically
  if (cache.size > 100) {
    const cutoff = Date.now() - CACHE_DURATION
    for (const [k, v] of cache.entries()) {
      if (v.timestamp < cutoff) {
        cache.delete(k)
      }
    }
  }
}

// Helper function to make API calls
const makeAPICall = async (url) => {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT)

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'User-Agent': 'RematchPro/2.0'
      },
      signal: controller.signal
    })

    clearTimeout(timeoutId)

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }

    return await response.json()
  } catch (error) {
    clearTimeout(timeoutId)
    throw error
  }
}

// Helper function to create API URLs
const createPlayerStatsUrl = (platform, identifier, forceRefresh = false, season = null) => {
  const baseUrl = `${API_BASE_URL}/api/rematch/${platform.toLowerCase()}/${identifier.toLowerCase()}`
  const params = new URLSearchParams()
  if (forceRefresh) params.append('refresh', 'true')
  if (season !== null && season !== undefined && season !== '') params.append('season', String(season))
  return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl
}

const createLeaderboardsUrl = (platform = 'all', page = 0, stat = null) => {
  const baseUrl = `${API_BASE_URL}/api/rematch/leaderboards`
  const params = new URLSearchParams()
  
  if (platform && platform !== 'all') {
    params.append('platform', platform.toLowerCase())
  }
  if (page && page > 0) {
    params.append('page', page.toString())
  }
  if (stat && stat !== 'all') {
    params.append('stat', stat.toLowerCase())
  }
  
  return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl
}

// Database fallback when API is unavailable
const getDatabaseFallback = async (platform, identifier) => {
  // External player stats tables are deprecated and don't exist
  console.warn('Database fallback requested, but external player stats tables are deprecated.')
  return {
    success: false,
    error: 'API unavailable and database fallback is no longer supported'
  }
}

const externalStatsService = {
  /**
   * Get player statistics from external API
   * @param {string} platform - Gaming platform (psn, xbox, steam)
   * @param {string} identifier - Player identifier
   * @param {boolean} forceRefresh - Force API call even if cached data exists
   * @returns {Promise<{success: boolean, data?: any, error?: string, cached?: boolean}>}
   */
  async getPlayerStats(platform, identifier, forceRefresh = false, season = null) {
    try {
      const seasonKey = (season === null || season === undefined || season === '') ? '' : `:s${season}`
      const cacheKey = `${platform}:${identifier}${seasonKey}`

      // Check local cache first (for rapid repeated requests)
      if (!forceRefresh) {
        const cachedData = getCachedResponse(cacheKey)
        if (cachedData) {
          return {
            success: true,
            data: cachedData,
            cached: true,
            source: 'local_cache'
          }
        }
      }

      // Call API directly
      const apiUrl = createPlayerStatsUrl(platform, identifier, forceRefresh, season)
      const apiData = await makeAPICall(apiUrl)

      // Validate API response structure
      if (!apiData || !apiData.player || !apiData.player.display_name) {
        // Try database fallback
        return await getDatabaseFallback(platform.toLowerCase(), identifier.toLowerCase())
      }

      // Cache the response locally
      setCachedResponse(cacheKey, apiData)

      return {
        success: true,
        data: apiData,
        cached: apiData.cached || false,
        source: 'api'
      }

    } catch (error) {
      console.error('API call failed:', error)
      // Try database fallback
      return await getDatabaseFallback(platform.toLowerCase(), identifier.toLowerCase())
    }
  },

  /**
   * Get leaderboards for specific stat type 
   * @deprecated This method is deprecated. The external player leaderboards tables no longer exist.
   * The external API only supports overall ranked leaderboards, not stat-specific ones.
   * Use getExternalLeaderboards() instead for overall rankings.
   * @param {string} statType - Type of statistic (goals, assists, saves, wins, mvps) - IGNORED
   * @param {number} limit - Maximum number of results
   * @param {string} platform - Optional platform filter
   * @returns {Promise<{success: boolean, data?: any[], error?: string}>}
   */
  async getLeaderboards(statType = 'goals', limit = 50, platform = null) {
    console.warn(`getLeaderboards(${statType}) is deprecated. External player leaderboards tables no longer exist. Use getExternalLeaderboards() for overall rankings.`);
    
    // Return empty results with deprecation warning
    return {
      success: true,
      data: [],
      deprecated: true,
      message: `Stat-specific leaderboards for '${statType}' are no longer supported. Use getExternalLeaderboards() for overall rankings.`
    };
  },

  /**
   * Get external leaderboards directly from API (live data)
   * @param {string} platform - Platform filter ('steam', 'psn', 'xbox', 'all')
   * @param {number} page - Page number (0-based)
   * @param {string} stat - Stat filter ('goals', 'assists', 'passes', 'saves', 'tackles', 'mvps', 'all' or null)
   * @returns {Promise<{success: boolean, data?: any, error?: string, cached?: boolean}>}
   */
  async getExternalLeaderboards(platform = 'all', page = 0, stat = null) {
    try {
      // Call API directly for leaderboards
      const apiUrl = createLeaderboardsUrl(platform, page, stat)
      const leaderboardData = await makeAPICall(apiUrl)

      // Validate the API response structure
      if (!leaderboardData || !Array.isArray(leaderboardData.entries)) {
        return {
          success: false,
          error: 'Invalid leaderboard data structure'
        }
      }

      // Return data directly
      return {
        success: true,
        data: leaderboardData,
        cached: false,
        source: 'api'
      }

    } catch (error) {
      return errorHandler(error, 'getExternalLeaderboards')
    }
  },

  /**
   * Search for players by name with enhanced results
   * @deprecated This method is deprecated. External player search functionality is no longer supported.
   * @param {string} searchTerm - Search term
   * @param {number} limit - Maximum results
   * @param {string} platform - Optional platform filter
   * @returns {Promise<{success: boolean, data?: any[], error?: string}>}
   */
  async searchPlayers(searchTerm, limit = 20, platform = null) {
    console.warn('searchPlayers() is deprecated. External player search functionality is no longer supported.');
    
    return {
      success: true,
      data: [],
      deprecated: true,
      message: 'Player search functionality is no longer supported.'
    }
  },

  /**
   * Get player match history
   * @deprecated This method is deprecated. External player match history is no longer stored in database.
   * @param {string} platform - Gaming platform
   * @param {string} identifier - Player identifier
   * @param {number} limit - Maximum matches to return
   * @returns {Promise<{success: boolean, data?: any[], error?: string}>}
   */
  async getPlayerMatchHistory(platform, identifier, limit = 10) {
    console.warn('getPlayerMatchHistory() is deprecated. External player match history is no longer stored in database.');
    
    return {
      success: true,
      data: [],
      deprecated: true,
      message: 'Player match history is no longer supported.'
    }
  },

  /**
   * Get statistics for multiple players (batch operation)
   * @param {Array<{platform: string, identifier: string}>} players - Array of player identifiers
   * @param {boolean} forceRefresh - Force refresh for all players
   * @returns {Promise<{success: boolean, data?: any[], errors?: any[]}>}
   */
  async getBatchPlayerStats(players, forceRefresh = false) {
    try {
      if (!Array.isArray(players) || players.length === 0) {
        return {
          success: true,
          data: []
        }
      }

      const batchSize = 5 // Process in batches to avoid overloading
      const results = []
      const errors = []

      for (let i = 0; i < players.length; i += batchSize) {
        const batch = players.slice(i, i + batchSize)
        
        const batchPromises = batch.map(async (player) => {
          try {
            const result = await this.getPlayerStats(player.platform, player.identifier, forceRefresh)
            return {
              platform: player.platform,
              identifier: player.identifier,
              ...result
            }
          } catch (error) {
            return {
              platform: player.platform,
              identifier: player.identifier,
              success: false,
              error: error.message
            }
          }
        })

        const batchResults = await Promise.allSettled(batchPromises)
        
        batchResults.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            results.push(result.value)
          } else {
            const player = batch[index]
            errors.push({
              platform: player?.platform || 'unknown',
              identifier: player?.identifier || 'unknown',
              error: result.reason.message
            })
          }
        })

        // Small delay between batches to be nice to the API
        if (i + batchSize < players.length) {
          await new Promise(resolve => setTimeout(resolve, 100))
        }
      }

      return {
        success: true,
        data: results,
        errors: errors.length > 0 ? errors : undefined
      }

    } catch (error) {
      return errorHandler(error, 'getBatchPlayerStats')
    }
  },

  /**
   * Get players that need refreshing (older than specified hours)
   * @deprecated This method is deprecated. External player refresh tracking is no longer supported.
   * @param {number} maxAgeHours - Maximum age in hours before refresh is needed
   * @param {number} limit - Maximum number of players to return
   * @returns {Promise<{success: boolean, data?: any[], error?: string}>}
   */
  async getPlayersNeedingRefresh(maxAgeHours = 4, limit = 50) {
    console.warn('getPlayersNeedingRefresh() is deprecated. External player refresh tracking is no longer supported.');
    
    return {
      success: true,
      data: [],
      deprecated: true,
      message: 'Player refresh tracking is no longer supported.'
    }
  },

  /**
   * Get player statistics history (changes over time)
   * @deprecated This method is deprecated. External player stats history is no longer stored in database.
   * @param {string} platform - Gaming platform
   * @param {string} identifier - Player identifier
   * @param {number} limit - Maximum history entries
   * @returns {Promise<{success: boolean, data?: any[], error?: string}>}
   */
  async getPlayerStatsHistory(platform, identifier, limit = 10) {
    console.warn('getPlayerStatsHistory() is deprecated. External player stats history is no longer stored in database.');
    
    return {
      success: true,
      data: [],
      deprecated: true,
      message: 'Player stats history is no longer supported.'
    }
  },

  /**
   * Refresh leaderboards manually
   * @deprecated This method is deprecated. External player leaderboards are no longer stored in database.
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async refreshLeaderboards() {
    console.warn('refreshLeaderboards() is deprecated. External player leaderboards are no longer stored in database.');
    
    return {
      success: true,
      deprecated: true,
      message: 'Manual leaderboard refresh is no longer supported.'
    }
  },

  /**
   * Get comprehensive player profile including stats and history
   * @param {string} platform - Gaming platform
   * @param {string} identifier - Player identifier
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async getPlayerProfile(platform, identifier) {
    try {
      // Get current stats from API
      const statsResult = await this.getPlayerStats(platform, identifier)
      if (!statsResult.success) {
        return statsResult
      }

      return {
        success: true,
        data: {
          currentStats: statsResult.data,
          matchHistory: statsResult.data?.match_history?.items || [],
          cached: statsResult.cached,
          hoursOld: statsResult.hoursOld
        }
      }

    } catch (error) {
      return errorHandler(error, 'getPlayerProfile')
    }
  },

  /**
   * Transform API data to UI-compatible format
   * @param {object} apiData - Raw API response data
   * @returns {object} Transformed data for UI consumption
   */
  transformForUI(apiData) {
    if (!apiData || !apiData.player) {
      return null
    }

    const { player, rank, rank3v3, lifetime_stats, rank_history, rank_history3v3, leaderboard_positions, chart } = apiData
    const allStats = lifetime_stats?.All || {}
    const rankedStats = lifetime_stats?.Ranked || {}
    const ranked3v3Stats = lifetime_stats?.Ranked3v3 || {}
    const quickmatch3v3Stats = lifetime_stats?.Quickmatch3v3 || {}
    const quickmatch4v4Stats = lifetime_stats?.Quickmatch4v4 || {}

    return {
      player: {
        id: `${player.platform}_${player.platform_id}`, // Unique ID for caching
        username: player.display_name,
        displayName: player.display_name,
        avatarUrl: player.avatar_asset ? `https://static.bigbrain.gg/rematch/ProfilePics/${player.avatar_asset}.png` : 'https://static.bigbrain.gg/rematch/ProfilePics/TX_UI_Launch_T1_Profileavatar_01.png',
        level: player.level || 0,
        rank: getRankDisplay(rank?.current_league, rank?.current_division),
        rank3v3: getRankDisplay(rank3v3?.current_league, rank3v3?.current_division),
        region: 'global',
        lastPlayed: player.last_updated_at,
        platform: player.platform?.toUpperCase() || 'UNKNOWN',
        title: player.title || '',
        platformId: player.platform_id
      },
      stats: {
        gamesPlayed: allStats.matches_played || 0,
        wins: allStats.wins || 0,
        losses: (allStats.matches_played || 0) - (allStats.wins || 0),
        winRate: allStats.matches_played > 0 ? ((allStats.wins / allStats.matches_played) * 100).toFixed(1) : 0,
        goals: allStats.goals || 0,
        assists: allStats.assists || 0,
        saves: allStats.saves || 0,
        shots: allStats.shots || 0,
        shotAccuracy: allStats.shot_ratio ? (allStats.shot_ratio * 100).toFixed(1) : 0,
        mvpCount: allStats.mvps || 0,
        averageScore: 0, // Not provided by new API
        playtime: 'Unknown', // Not provided by new API
        tackles: allStats.tackles || 0,
        interceptedPasses: allStats.intercepted_passes || 0,
        streak: {
          current: 0, // Not provided by new API
          best: 0, // Not provided by new API
          type: 'unknown'
        },
        seasonal: {
          rank: getRankDisplay(rank?.current_league, rank?.current_division),
          rank3v3: getRankDisplay(rank3v3?.current_league, rank3v3?.current_division),
          divisionProgress: 0, // Not provided by new API
          mmr: 0, // Not provided by new API
          peakRank: getRankDisplay(rank?.current_league, rank?.current_division),
          peakRank3v3: getRankDisplay(rank3v3?.current_league, rank3v3?.current_division)
        }
      },
      gameModeStats: {
        ranked: {
          gamesPlayed: rankedStats.matches_played || 0,
          wins: rankedStats.wins || 0,
          losses: (rankedStats.matches_played || 0) - (rankedStats.wins || 0),
          winRate: rankedStats.matches_played > 0 ? ((rankedStats.wins / rankedStats.matches_played) * 100).toFixed(1) : 0,
          goals: rankedStats.goals || 0,
          assists: rankedStats.assists || 0,
          saves: rankedStats.saves || 0,
          mvpCount: rankedStats.mvps || 0,
          averageScore: 0,
          shotAccuracy: rankedStats.shot_ratio ? (rankedStats.shot_ratio * 100).toFixed(1) : 0
        },
        ranked3v3: {
          gamesPlayed: ranked3v3Stats.matches_played || 0,
          wins: ranked3v3Stats.wins || 0,
          losses: (ranked3v3Stats.matches_played || 0) - (ranked3v3Stats.wins || 0),
          winRate: ranked3v3Stats.matches_played > 0 ? ((ranked3v3Stats.wins / ranked3v3Stats.matches_played) * 100).toFixed(1) : 0,
          goals: ranked3v3Stats.goals || 0,
          assists: ranked3v3Stats.assists || 0,
          saves: ranked3v3Stats.saves || 0,
          mvpCount: ranked3v3Stats.mvps || 0,
          averageScore: 0,
          shotAccuracy: ranked3v3Stats.shot_ratio ? (ranked3v3Stats.shot_ratio * 100).toFixed(1) : 0
        },
        casual: {
          gamesPlayed: (quickmatch3v3Stats.matches_played || 0) + (quickmatch4v4Stats.matches_played || 0),
          wins: (quickmatch3v3Stats.wins || 0) + (quickmatch4v4Stats.wins || 0),
          losses: ((quickmatch3v3Stats.matches_played || 0) - (quickmatch3v3Stats.wins || 0)) +
                  ((quickmatch4v4Stats.matches_played || 0) - (quickmatch4v4Stats.wins || 0)),
          winRate: 0, // Calculated below
          goals: (quickmatch3v3Stats.goals || 0) + (quickmatch4v4Stats.goals || 0),
          assists: (quickmatch3v3Stats.assists || 0) + (quickmatch4v4Stats.assists || 0),
          saves: (quickmatch3v3Stats.saves || 0) + (quickmatch4v4Stats.saves || 0),
          mvpCount: (quickmatch3v3Stats.mvps || 0) + (quickmatch4v4Stats.mvps || 0),
          averageScore: 0
        },
        tournament: {
          gamesPlayed: 0, // No tournament data in new API
          wins: 0,
          losses: 0,
          winRate: 0,
          goals: 0,
          assists: 0,
          saves: 0,
          mvpCount: 0,
          averageScore: 0
        }
      },
      rankHistory: rank_history || [],
      rankHistory3v3: rank_history3v3 || [],
      leaderboardPositions: leaderboard_positions || [],
      chartData: chart || null,
      recentMatches: [], // Will be populated from match_history if available
      apiData: apiData, // Store original API response
      cached: apiData.cached || false,
      lastUpdated: player.last_updated_at || new Date().toISOString()
    }
  }
}

// Calculate casual win rate
const transformedStats = externalStatsService.transformForUI
if (transformedStats && transformedStats.gameModeStats?.casual) {
  const casual = transformedStats.gameModeStats.casual
  if (casual.gamesPlayed > 0) {
    casual.winRate = ((casual.wins / casual.gamesPlayed) * 100).toFixed(1)
  }
}

// Export individual functions for backward compatibility
export const getPlayerStats = externalStatsService.getPlayerStats.bind(externalStatsService)
export const searchPlayers = externalStatsService.searchPlayers.bind(externalStatsService)
export const transformForUI = externalStatsService.transformForUI.bind(externalStatsService)

export { externalStatsService }